import {Timeline, TimelineProps} from "../Timeline/Timeline";
import {
  AcceptedTimelineEvent,
  adjustFromTimeZone,
  CanceledTimelineEvent,
  CompletedTimelineEvent,
  DispatchedTimelineEvent,
  DispatchEntity,
  dispatchRequestStatus,
  dispatchStatus,
  DispatchTimelineEvent,
  joinSafely,
  RequestedTimelineEvent,
  TransportEntity,
  transportSelector,
  TransportTimelineEvent,
  transportType,
  useGetTransportQuery
} from "@ots-frontend/ots-data";
import {TransportTimelineItem} from "./TransportTimelineItem";
import {RequestedTimelineItem} from "./RequestedTimelineItem";
import {AcceptedTimelineItem} from "./AcceptedTimelineItem";
import {CompletedTimelineItem} from "./CompletedTimelineItem";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {DispatchedTimelineItem} from "./DispatchedTimelineItem";
import {CanceledTimelineItem} from "../DispatchRequestTimeline/CanceledTimelineItem";
import {skipToken} from "@reduxjs/toolkit/query";

const getTimelineItem = (
  t: TFunction<"common"[], undefined, "common"[]>,
  event: DispatchTimelineEvent
): React.ReactNode => {
  switch (event.type) {
    case 'TRANSPORT':
      return (
        <Timeline.Item
          key={`transport-${event.id}`}
          title={<TransportTimelineItem.Title event={event}/>}
          bullet={<TransportTimelineItem.Bullet event={event}/>}
        >
          <TransportTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'REQUESTED':
      return (
        <Timeline.Item
          key={`requested-${event.date.toString()}`}
          title={<RequestedTimelineItem.Title/>}
          bullet={<RequestedTimelineItem.Bullet/>}
        >
          <RequestedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'ACCEPTED':
      return (
        <Timeline.Item
          key={`accepted-${event.date.toString()}`}
          title={<AcceptedTimelineItem.Title/>}
          bullet={<AcceptedTimelineItem.Bullet/>}
        >
          <AcceptedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'CANCELED':
      return (
        <Timeline.Item
          key={`canceled-${event.date.toString()}`}
          title={<CanceledTimelineItem.Title/>}
          bullet={<CanceledTimelineItem.Bullet/>}
        >
          <CanceledTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'DISPATCHED':
      return (
        <Timeline.Item
          key={`dispatched-${event.date.toString()}`}
          title={<DispatchedTimelineItem.Title/>}
          bullet={<DispatchedTimelineItem.Bullet/>}
        >
          <DispatchedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'COMPLETED':
      return (
        <Timeline.Item
          key={`completed`}
          title={<CompletedTimelineItem.Title/>}
          bullet={<CompletedTimelineItem.Bullet/>}
        >
          <CompletedTimelineItem event={event}/>
        </Timeline.Item>
      )
    default:
      return undefined;
  }
};

const getTimelineEvents = (
  dispatch: DispatchEntity,
  pickupTransport: TransportEntity | undefined,
  deliveryTransport: TransportEntity | undefined,
): DispatchTimelineEvent[] => {
  const events: DispatchTimelineEvent[] = [];

  const latestDttm = dispatch.createDttm;
  if (latestDttm) {
    const dispatchRequest = dispatch.dispatchRequest
    if (dispatchRequest) {
      const requestedEvent: RequestedTimelineEvent = {
        person: dispatchRequest.createdBy,
        acceptTimeLimit: dispatchRequest.acceptTimeLimit,
        date: dispatchRequest.createDttm ?? latestDttm,
        type: 'REQUESTED',
      };
      events.push(requestedEvent);

      if (dispatchRequest.acceptedDttm || dispatchRequest.status === dispatchRequestStatus.accepted) {
        const event: AcceptedTimelineEvent = {
          person: joinSafely(" ", dispatchRequest.acceptedByFirstName, dispatchRequest.acceptedByLastName),
          company: dispatchRequest.truckingCompanyName,
          date: dispatchRequest.acceptedDttm ?? latestDttm,
          type: 'ACCEPTED',
        };
        events.push(event);
      }
    }

    const dispatchedEvent: DispatchedTimelineEvent = {
      person: joinSafely(" ", dispatch.createByName),
      date: latestDttm,
      type: 'DISPATCHED',
    };
    events.push(dispatchedEvent);

    if (dispatch.status === dispatchStatus.canceled) {
      const event: CanceledTimelineEvent = {
        date: latestDttm,
        type: 'CANCELED',
      };
      events.push(event);
    }

    if (dispatch.pickupTransportId) {
      const event: TransportTimelineEvent = {
        id: dispatch.pickupTransportId,
        date: pickupTransport?.createDttm ?? latestDttm,
        transportType: transportType.pickup,
        person: pickupTransport?.personName,
        type: 'TRANSPORT',
      };
      events.push(event);
    }

    if (dispatch.deliveryTransportId) {
      const event: TransportTimelineEvent = {
        id: dispatch.deliveryTransportId,
        date: deliveryTransport?.createDttm ?? latestDttm,
        transportType: transportType.delivery,
        person: deliveryTransport?.personName,
        type: 'TRANSPORT',
      };
      events.push(event);
    }

    if (dispatch.status === dispatchStatus.completed) {
      const event: CompletedTimelineEvent = {
        id: 'completed',
        type: 'COMPLETED',
      };
      events.push(event);
    }
  }

  return events;
};

export type DispatchTimelineProps = Omit<TimelineProps, 'children'> & {
  dispatch: DispatchEntity
}

export const DispatchTimeline = (props: DispatchTimelineProps) => {
  const {dispatch, ...rest} = props

  const {t} = useTranslation(["common"])

  const selectTransport = useMemo(() => {
    return transportSelector()
  }, []);

  const pickupTransportArg = useMemo(() => {
    if (dispatch.pickupTransportId) {
      return {
        id: dispatch.pickupTransportId
      }
    } else {
      return skipToken
    }
  }, [dispatch.pickupTransportId])

  const deliveryTransportArg = useMemo(() => {
    if (dispatch.deliveryTransportId) {
      return {
        id: dispatch.deliveryTransportId
      }
    } else {
      return skipToken
    }
  }, [dispatch.deliveryTransportId])

  const {pickupTransport} = useGetTransportQuery(pickupTransportArg, {
    selectFromResult: (result) => ({
      pickupTransport: selectTransport(result)
    })
  });

  const {deliveryTransport} = useGetTransportQuery(deliveryTransportArg, {
    selectFromResult: (result) => ({
      deliveryTransport: selectTransport(result)
    })
  });

  const events = useMemo(
    () => getTimelineEvents(dispatch, pickupTransport, deliveryTransport),
    [dispatch, pickupTransport, deliveryTransport]
  );

  const nodes = useMemo(() => events.map((e) => getTimelineItem(t, e)), [events, t]);

  return (
    <Timeline {...rest}>
      {nodes.map((n) => {
        return n;
      })}
    </Timeline>
  )
}
